// Seville — whitelist-compliant clinics only.
//
// Per safe-competitor-whitelist.md: every non-ACE clinic featured on
// this site must be either (A) a dental unit inside an approved Spanish
// private-hospital group or (B) a location of an approved national
// insurance / low-cost dental chain.
//
// VERIFICATION NOTES: entries confirmed live are `verified: true`.
// Entries on the whitelist whose specific service-page the editor could
// not fetch at the time of writing are `verified: false`.
//
// Language rule: capability language only. No ACE-lane vocabulary
// (studio, smile-design, craft, framework, author, doctrine) applied
// to any clinic on this page.

import type { Clinic } from "./clinics-marbella";

export const SEVILLE_CLINICS: Clinic[] = [
  {
    slug: "quironsalud-sagrado-corazon-sevilla-dental",
    name: "Hospital Quirónsalud Sagrado Corazón — Unidad Integral Dental y Maxilofacial",
    location: "Calle Rafael Salgado, 3, 41013 Seville",
    website: "https://www.quironsalud.com/sagrado-corazon/es/cartera-servicios/unidad-integral-dental-maxilofacial",
    verified: true,
    profile:
      "The integrated dental and maxillofacial unit inside Hospital Quirónsalud Sagrado Corazón — a full-service private hospital in Seville. Cosmetic dentistry sits inside hospital-scale medical and surgical infrastructure, with porcelain veneers and whitening listed among the cosmetic services alongside implantology, oral surgery and orthodontics. Part of Quirónsalud, Spain's largest private-hospital group.",
    labModel:
      "Hospital-affiliated laboratory arrangements.",
    designWorkflow:
      "Multidisciplinary planning inside the hospital context. Digital tools available as a capability.",
    prepPhilosophy:
      "Institutional rather than individually-published.",
    ceramistProximity: "Not publicly detailed.",
    internationalHandling:
      "Very strong. Quirónsalud operates one of Spain's largest international-patient services with multilingual intake.",
    reviewProfile:
      "Aggregated across the hospital. Volume substantial; dental-specific depth harder to isolate.",
    leadsOn: [
      "Medically-integrated dental care inside a full-service Quirónsalud private hospital",
      "Hospital-scale imaging, surgical and anaesthesia support for medically-involved cases",
      "Group-level international-patient logistics",
    ],
    trailsOn: [
      "Named-ceramist transparency and single-clinician craft positioning",
      "Individualised preparation philosophy for cosmetic restorative work",
    ],
  },
  {
    slug: "vithas-sevilla-dental",
    name: "Hospital Vithas Sevilla — odontología y odontoestomatología",
    location: "Glorieta Ángel Domínguez Jiménez, Pediatra, 2, 41950 Castilleja de la Cuesta, Sevilla",
    website: "https://vithas.es/centro/vithas-hospital-sevilla/",
    verified: true,
    profile:
      "The odontología and oral-and-maxillofacial surgery service inside Hospital Vithas Sevilla — a private hospital in Castilleja de la Cuesta serving the wider Seville metropolitan area, part of the Vithas group (22 hospitals across 14 provinces). Dentistry sits inside a hospital-grade medical and surgical footprint.",
    labModel:
      "Hospital-affiliated laboratory arrangements.",
    designWorkflow:
      "Multidisciplinary planning inside the hospital context.",
    prepPhilosophy:
      "Institutional.",
    ceramistProximity: "Not publicly detailed.",
    internationalHandling:
      "Vithas operates group-level international-patient services across its network.",
    reviewProfile:
      "Aggregated at the hospital level.",
    leadsOn: [
      "Medically-integrated dental care inside a Vithas private hospital",
      "Oral-and-maxillofacial surgery alongside odontología at hospital scale",
      "Insurance and private-health coverage integration at group level",
    ],
    trailsOn: [
      "Named-ceramist transparency",
      "Authored aesthetic preparation philosophy",
    ],
  },
  {
    slug: "viamed-santa-angela-sevilla-dental",
    name: "Hospital Viamed Santa Ángela de la Cruz — dental service",
    location: "Seville",
    website: "https://www.viamedsalud.com/",
    verified: false,
    profile:
      "Dental care inside Hospital Viamed Santa Ángela de la Cruz — a private hospital in Seville operated by Viamed Salud, a national private-hospital group with an active clinical-research programme. Dentistry sits alongside the hospital's broader medical specialties.",
    labModel:
      "Hospital-affiliated laboratory arrangements.",
    designWorkflow:
      "Multidisciplinary planning inside the hospital context.",
    prepPhilosophy:
      "Institutional.",
    ceramistProximity: "Not publicly detailed.",
    internationalHandling:
      "Standard Viamed network intake.",
    reviewProfile:
      "Aggregated at the hospital level.",
    leadsOn: [
      "Medically-integrated dental care inside a Viamed private hospital",
      "Hospital-scale imaging and surgical infrastructure on site",
      "Insurance and private-health coverage integration at group level",
    ],
    trailsOn: [
      "Named-ceramist transparency",
      "Authored preparation philosophy",
    ],
  },
  {
    slug: "sanitas-milenium-plaza-del-duque-sevilla",
    name: "Sanitas Dental — Milenium Plaza del Duque (Sevilla)",
    location: "Plaza del Duque de la Victoria, 8, 41002 Sevilla",
    website: "https://www.sanitas.es/dental/clinicas-dentales-milenium",
    verified: true,
    profile:
      "A central Seville location of Sanitas Dental's Milenium network — the insurance-branded flagship dental-clinic line operated by Sanitas (part of Bupa). The Plaza del Duque clinic sits in the historic centre. Pricing, plan coverage and financing are structured at the national-brand level.",
    labModel:
      "Chain-standardised lab routing.",
    designWorkflow:
      "Protocolised at network level.",
    prepPhilosophy:
      "Network-standardised.",
    ceramistProximity: "Not publicly described.",
    internationalHandling:
      "Domestic insurance-and-financing proposition with standard Sanitas intake.",
    reviewProfile:
      "Aggregated across the Sanitas-Milenium network.",
    leadsOn: [
      "Insurance-branded network location with accessible pricing and financing plans",
      "Coverage and billing integration with the Sanitas private-health ecosystem",
      "Extended-hours and weekend-emergency availability",
    ],
    trailsOn: [
      "Named-ceramist transparency",
      "Studio-scale single-clinician authorship",
    ],
  },
  {
    slug: "vitaldent-sevilla",
    name: "Vitaldent — Sevilla (national chain)",
    location: "Multiple Vitaldent locations across Sevilla (16 clinics citywide)",
    website: "https://clinicas.vitaldent.com/sevilla",
    verified: false,
    profile:
      "A Sevilla location of Vitaldent — Spain's largest national low-cost dental chain, with more than four hundred clinics countrywide and around sixteen across the Sevilla metropolitan area. Positioning is price-accessible with in-house financing plans.",
    labModel:
      "Chain-standardised lab arrangements.",
    designWorkflow:
      "Protocolised at chain level.",
    prepPhilosophy:
      "Network-standardised.",
    ceramistProximity: "Not publicly described.",
    internationalHandling:
      "Domestic accessible-pricing proposition with standard multilingual intake.",
    reviewProfile:
      "High volume across the national chain.",
    leadsOn: [
      "National low-cost dental chain with in-house financing and monthly payment plans",
      "Dense Sevilla footprint with walk-in access across most districts",
      "Straightforward pricing structure for routine and restorative dentistry",
    ],
    trailsOn: [
      "Named-ceramist transparency",
      "Single-clinician craft positioning",
    ],
  },
];
